import type { DataTableColumn } from '@easydoctor/ui';
import {
  DISCARD_REASON_LABELS,
  REPLY_RECEIVED_LABEL,
  TICKET_CHANNEL_LABELS,
  type TicketQueueItem,
} from '@easyhelp/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import {
  PriorityChip,
  StateChip,
  UNASSIGNED_LABEL,
  collaboratorsLabel,
  formatDateTime,
  formatElapsed,
  formatTimeOrDate,
  personName,
} from '../../components/ticket';

/** Id della colonna che ospita il link della riga: il codice è il nome del link per i lettori di schermo. */
export const QUEUE_LINK_COLUMN = 'code';

/** Il codice del ticket nel carattere monospaziato del tema. */
function ticketCode(item: TicketQueueItem, variant: 'body2' | 'caption' = 'body2') {
  return (
    <Typography
      variant={variant}
      component="span"
      sx={{
        display: 'block',
        fontFamily: (theme) => theme.typography.fontFamilyMonospace,
        fontWeight: 500,
        color: variant === 'body2' ? 'text.primary' : 'text.secondary',
        whiteSpace: 'nowrap',
      }}
    >
      {item.code}
    </Typography>
  );
}

/** Il pallino di «Risposta ricevuta» (#122): decorativo, il testo lo dice l'etichetta. */
function replyDot() {
  return (
    <Box
      component="span"
      aria-hidden
      data-reply-dot
      sx={{
        display: 'inline-block',
        width: 8,
        height: 8,
        mr: 2,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        verticalAlign: 'middle',
      }}
    />
  );
}

/**
 * «Risposta ricevuta · 14:32» (#122): l'ultimo messaggio verso l'esterno è del segnalante. L'ora se è
 * di oggi, altrimenti la data; data e ora intere nel `title`.
 */
function replyReceivedLabel(item: TicketQueueItem, now: Date) {
  if (!item.replyReceivedAt) return null;
  return (
    <Typography
      variant="caption"
      component="span"
      title={`Risposta del segnalante ricevuta il ${formatDateTime(item.replyReceivedAt)}`}
      sx={{ display: 'block', color: 'primary.dark', fontWeight: 600 }}
    >
      {/* Con l'anno («· 30/12/2025») non sta in una riga della colonna: va a capo prima del punto, mai dentro la data. */}
      {REPLY_RECEIVED_LABEL}{' '}
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        · {formatTimeOrDate(item.replyReceivedAt, now)}
      </Box>
    </Typography>
  );
}

/**
 * Pallino di «Risposta ricevuta», «Pre-accesso» e titolo, nel flusso del testo: un titolo lungo va a capo
 * dopo il chip, non sotto. Con «Risposta ricevuta» il titolo è in grassetto (#122).
 */
function ticketTitle(item: TicketQueueItem) {
  const replied = Boolean(item.replyReceivedAt);
  return (
    <Typography
      variant="body2"
      component="span"
      color="text.primary"
      sx={{ display: 'block', fontWeight: replied ? 700 : 500, lineHeight: 1.4 }}
    >
      {replied && replyDot()}
      {item.channel === 'pre-accesso' && (
        <Chip
          size="small"
          variant="outlined"
          label={TICKET_CHANNEL_LABELS['pre-accesso']}
          sx={{ mr: 2, verticalAlign: 'text-bottom', height: 'auto' }}
        />
      )}
      {item.title}
    </Typography>
  );
}

/** Committente, responsabile («Non assegnato» in evidenza) e collaboratori, sotto il titolo. */
function rowMeta(item: TicketQueueItem) {
  const collaborators = collaboratorsLabel(item.collaborators);
  return (
    <Typography variant="caption" component="span" color="text.secondary" sx={{ display: 'block' }}>
      {item.committente ?? 'Committente non identificato'}
      {' · '}
      {item.assignee ? (
        personName(item.assignee)
      ) : (
        <Box component="span" sx={{ color: 'warning.dark', fontWeight: 500 }}>
          {UNASSIGNED_LABEL}
        </Box>
      )}
      {collaborators && ` ${collaborators}`}
    </Typography>
  );
}

/**
 * Stato del ticket; sotto, sugli scartati, il motivo dello scarto (DEC-002 regola 3, #123: «Duplicato di
 * EC-2026-0001», «Spam», «Non pertinente») e, sugli aperti, «Risposta ricevuta · ora» (#122), che arriva
 * già pronta perché sul telefono sta nella colonna «Ticket».
 */
function stateCell(item: TicketQueueItem, reply: ReactNode) {
  if (!item.discard && !reply) return <StateChip state={item.state} />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
      <StateChip state={item.state} />
      {item.discard && <DiscardReason discard={item.discard} />}
      {reply}
    </Box>
  );
}

/** Il motivo dello scarto sotto lo stato (#123). */
function DiscardReason({ discard }: { discard: NonNullable<TicketQueueItem['discard']> }) {
  return (
    <Typography variant="caption" component="span" color="text.secondary">
      {DISCARD_REASON_LABELS[discard.reason]}
      {discard.original && (
        <>
          {' di '}
          {/* Il codice non si spezza al trattino: «EC-2026-» e «0002» su due righe non si leggono. */}
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {discard.original}
          </Box>
        </>
      )}
    </Typography>
  );
}

/**
 * `wide` da `sm` (768px); `narrow` sotto, sul telefono: codice e segnalazione in una sola colonna
 * «Ticket», perché la colonna del codice da sola lascerebbe al titolo meno di 100px.
 */
export type QueueColumnsLayout = 'wide' | 'narrow';

/**
 * Colonne della coda (PRD-004 US-1, #105) per la `DataTable` di `@easydoctor/ui`, come
 * `patientListColumns.tsx` di EasyCare: le priorità decidono cosa resta visibile quando la tabella
 * è stretta (`secondary` da `sm`, `tertiary` da `md`; il resto va nella riga espandibile).
 *
 * - **Codice** (`primary`, mono): ospita il link della riga ({@link QUEUE_LINK_COLUMN}).
 * - **Segnalazione** (`primary`): «Pre-accesso», titolo e sotto «committente · responsabile».
 *   Senza larghezza: assorbe lo spazio che resta e va a capo invece di troncare.
 * - **Priorità**, **Stato** (`secondary`; sotto, il motivo sugli scartati, #123, e «Risposta ricevuta ·
 *   ora», #122), **Aperto** (`tertiary`, tempo dall'apertura, la data nel `title`).
 * - Sotto `sm` (`narrow`) Codice e Segnalazione sono una colonna sola, «Ticket», con il codice sopra il
 *   titolo; il link della riga la comprende tutta. Priorità, Stato e Aperto vanno nella riga espandibile,
 *   e «Risposta ricevuta» resta nella colonna «Ticket».
 * - «Risposta ricevuta» (#122): pallino e titolo in grassetto nella segnalazione, etichetta con l'ora.
 *
 * È una funzione e non una costante perché il tempo trascorso dipende da `now`. Una colonna nuova o
 * un'etichetta in più in una cella si aggiunge qui senza toccare la pagina.
 */
export function queueColumns(now: Date, layout: QueueColumnsLayout = 'wide'): DataTableColumn<TicketQueueItem>[] {
  const lead: DataTableColumn<TicketQueueItem>[] =
    layout === 'wide'
      ? [
          {
            id: QUEUE_LINK_COLUMN,
            label: 'Codice',
            priority: 'primary',
            width: '148px',
            accessor: (item) => ticketCode(item),
          },
          {
            id: 'segnalazione',
            label: 'Segnalazione',
            priority: 'primary',
            accessor: (item) => (
              <Box sx={{ minWidth: 0 }}>
                {ticketTitle(item)}
                {rowMeta(item)}
              </Box>
            ),
          },
        ]
      : [
          {
            id: QUEUE_LINK_COLUMN,
            label: 'Ticket',
            priority: 'primary',
            // Sul telefono lo Stato va nella riga espandibile: «Risposta ricevuta» resta qui, sotto il titolo.
            accessor: (item) => (
              <Box sx={{ minWidth: 0 }}>
                {ticketCode(item, 'caption')}
                {ticketTitle(item)}
                {rowMeta(item)}
                {replyReceivedLabel(item, now)}
              </Box>
            ),
          },
        ];
  return [
    ...lead,
    {
      id: 'priority',
      label: 'Priorità',
      priority: 'secondary',
      width: '128px',
      accessor: (item) => <PriorityChip priority={item.priority} />,
    },
    {
      id: 'state',
      label: 'Stato',
      priority: 'secondary',
      width: '196px',
      accessor: (item) => stateCell(item, layout === 'wide' && replyReceivedLabel(item, now)),
    },
    {
      id: 'opened',
      label: 'Aperto',
      priority: 'tertiary',
      width: '96px',
      align: 'right',
      accessor: (item) => (
        <Box component="span" title={`Aperto il ${formatDateTime(item.receivedAt)}`} sx={{ whiteSpace: 'nowrap' }}>
          {formatElapsed(item.receivedAt, now)}
        </Box>
      ),
    },
  ];
}
